// Single source of truth for the identity document-type matrix, mirroring the backend
// (coloca-nest: src/coloca/identities/constants/identity-document-types.ts), which the
// `@IsValidDocumentType(countryField, category)` decorator enforces on every identity payload.
//
// Why it lives here: the API rejects a document type that is not valid for the country it is issued in, so
// any selector offering a fixed list produces 400s for most countries. Mirroring the matrix lets the form
// offer exactly what the API accepts.
//
// Categories mirror the decorator's:
//   - `personal`: document a natural person presents. What the individual identity is validated against.
//   - `fiscal`:   tax identifier of a company.
//   - `all`:      union of both.
//
// Countries outside the matrix fall back to the generic lists, exactly like `resolveAllowedTypes` on the
// backend — `@IsCountryCode` accepts the full ISO 3166-1 alpha-3 set, not just the six mapped ones, so every
// country in a selector stays usable.
//
// Keep in sync when the backend adds a country or a document type.

/** ISO 3166-1 alpha-3 codes with a country-specific document matrix. Others use the generic lists. */
export const SUPPORTED_IDENTITY_COUNTRIES = ["ARG", "BRA", "CHL", "COL", "MEX", "PER"] as const;

export type IdentityCountryCode = (typeof SUPPORTED_IDENTITY_COUNTRIES)[number];

export type DocumentTypeCategory = "personal" | "fiscal" | "all";

/** Every document code the backend can accept. Drives the i18n label key, so all of them need a label. */
export type DocumentTypeCode =
  | "CC"
  | "CE"
  | "PPT"
  | "PASSPORT"
  | "DNI"
  | "LE"
  | "LC"
  | "CI"
  | "RG"
  | "CNH"
  | "INE"
  | "NATIONAL_ID"
  | "DRIVER_LICENSE"
  | "RESIDENCE_PERMIT"
  | "NIT"
  | "RFC"
  | "CUIL"
  | "CPF"
  | "CNPJ"
  | "RUC"
  | "RUT"
  | "TAX_ID";

// Order matters: the first entry of a list is what the UI preselects when the country changes, so each list
// keeps the backend's order (most common document first) rather than being sorted alphabetically.
//
// Never empty a country's list to "remove" a document: both this file and the backend then fall back to the
// generic lists, and those codes are absent from that country's `all` — so every payload for it would 400.
// Removing a code means moving it to the other category, never leaving a hole.

const PERSONAL_DOCUMENT_TYPES: Record<IdentityCountryCode, readonly DocumentTypeCode[]> = {
  ARG: ["DNI", "LE", "LC", "CI", "PASSPORT"],
  BRA: ["RG", "CNH", "CPF"],
  CHL: ["CI"],
  COL: ["CC", "CE", "PPT", "PASSPORT"],
  MEX: ["INE", "PASSPORT"],
  PER: ["DNI", "CE", "PASSPORT"],
};

const FISCAL_DOCUMENT_TYPES: Record<IdentityCountryCode, readonly DocumentTypeCode[]> = {
  ARG: ["CUIL"],
  // CPF is the natural-person register (Pessoa Física); a company is identified by its CNPJ (Pessoa
  // Jurídica). Note this narrows what the UI offers: the API still accepts a CPF on a business (every DTO
  // validates against `all`), but no form here does, so a Brazilian company can only be created with a
  // CNPJ. That is correct for every registered company, a formalized sole trader (MEI) included — a MEI is
  // issued a CNPJ on formalization.
  BRA: ["CNPJ"],
  CHL: ["RUT"],
  COL: ["NIT"],
  MEX: ["RFC"],
  PER: ["RUC"],
};

const ALL_DOCUMENT_TYPES: Record<IdentityCountryCode, readonly DocumentTypeCode[]> = {
  ARG: [...PERSONAL_DOCUMENT_TYPES.ARG, ...FISCAL_DOCUMENT_TYPES.ARG],
  BRA: [...PERSONAL_DOCUMENT_TYPES.BRA, ...FISCAL_DOCUMENT_TYPES.BRA],
  CHL: [...PERSONAL_DOCUMENT_TYPES.CHL, ...FISCAL_DOCUMENT_TYPES.CHL],
  COL: [...PERSONAL_DOCUMENT_TYPES.COL, ...FISCAL_DOCUMENT_TYPES.COL],
  MEX: [...PERSONAL_DOCUMENT_TYPES.MEX, ...FISCAL_DOCUMENT_TYPES.MEX],
  PER: [...PERSONAL_DOCUMENT_TYPES.PER, ...FISCAL_DOCUMENT_TYPES.PER],
};

const GENERIC_PERSONAL_DOCUMENT_TYPES: readonly DocumentTypeCode[] = [
  "PASSPORT",
  "NATIONAL_ID",
  "DRIVER_LICENSE",
  "RESIDENCE_PERMIT",
];

const GENERIC_FISCAL_DOCUMENT_TYPES: readonly DocumentTypeCode[] = ["TAX_ID"];

const BY_CATEGORY: Record<DocumentTypeCategory, Record<IdentityCountryCode, readonly DocumentTypeCode[]>> = {
  personal: PERSONAL_DOCUMENT_TYPES,
  fiscal: FISCAL_DOCUMENT_TYPES,
  all: ALL_DOCUMENT_TYPES,
};

const GENERIC_BY_CATEGORY: Record<DocumentTypeCategory, readonly DocumentTypeCode[]> = {
  personal: GENERIC_PERSONAL_DOCUMENT_TYPES,
  fiscal: GENERIC_FISCAL_DOCUMENT_TYPES,
  all: [...GENERIC_PERSONAL_DOCUMENT_TYPES, ...GENERIC_FISCAL_DOCUMENT_TYPES],
};

const isSupportedCountry = (country: string): country is IdentityCountryCode =>
  (SUPPORTED_IDENTITY_COUNTRIES as readonly string[]).includes(country);

/**
 * Document types the backend accepts for a country + category, in preselect order. Countries without a
 * specific matrix get the generic list, so the result is never empty and `[0]` is always a safe default.
 */
export const documentTypesFor = (
  country: string | undefined,
  category: DocumentTypeCategory,
): readonly DocumentTypeCode[] => {
  const specific = country && isSupportedCountry(country) ? BY_CATEGORY[category][country] : undefined;
  return specific && specific.length > 0 ? specific : GENERIC_BY_CATEGORY[category];
};

/**
 * Fixed-length rules, for the codes whose length is a checkable property. A code absent from this map has no
 * fixed length (RG varies by state, passports are alphanumeric, …).
 *
 * `strip` removes everything that is not part of the number, so the count is taken over the characters that
 * carry meaning — the backend stores the number without separators, which makes `12.345.678/0001-95` and
 * `12345678000195` the same document, and both must pass.
 *
 * **A CPF is numeric; a CNPJ is not.** Since the Receita Federal's 2026 rollout a CNPJ is twelve alphanumeric
 * positions plus two numeric check digits, still printed as `12.ABC.345/01DE-35`. Counting digits alone would
 * reject one — and would contradict the schemas, whose character set already admits letters. Hence one rule
 * per code rather than one shared "count the digits".
 *
 * This is a client-side courtesy, not a contract: the backend validates the character set but no length for
 * any country, so a wrong length is accepted server-side. Keeping the check here spares the user a document
 * that is silently unusable downstream.
 */
export const DOCUMENT_LENGTH_RULES = {
  CPF: { length: 11, strip: /[^0-9]/g },
  CNPJ: { length: 14, strip: /[^A-Za-z0-9]/g },
} as const satisfies Partial<Record<DocumentTypeCode, { length: number; strip: RegExp }>>;

/**
 * The document codes that carry a fixed-length rule. Each one needs an error message — see the clients
 * module.
 */
export type FixedLengthDocumentCode = keyof typeof DOCUMENT_LENGTH_RULES;

/** Whether a typed number satisfies its document type's fixed length. Codes without a rule always pass. */
export const isDocumentNumberLengthValid = (code: string, value: string): boolean => {
  const rule = DOCUMENT_LENGTH_RULES[code as FixedLengthDocumentCode];
  return rule === undefined || value.replace(rule.strip, "").length === rule.length;
};
